import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { crmApi } from "../api/client";
import { useSelectedProject } from "../contexts/SelectedProjectProvider";
import { useSelectedCompany } from "../contexts/SelectedCompanyProvider";

const PIPELINES_KEY = ["crm", "pipelines"];

function scopeParams(companyQueryParams) {
  return companyQueryParams && Object.keys(companyQueryParams).length ? companyQueryParams : {};
}

export function usePipelines() {
  const { projectId } = useSelectedProject();
  const { companyQueryParams } = useSelectedCompany();
  const scope = scopeParams(companyQueryParams);
  return useQuery({
    queryKey: [...PIPELINES_KEY, { projectId, ...scope }],
    queryFn: async () => (await crmApi.get("/pipelines", {
      params: { ...(projectId ? { projectId } : {}), ...scope },
    })).data,
  });
}

// Centralized invalidator. Any pipeline mutation (move, bulk add, stage CRUD,
// remove) affects the Leads list (stage-derived rollups + activity feed),
// dashboard counters, and per-pipeline analytics. Fan out so all surfaces
// stay in sync without waiting on staleTime.
function invalidate(qc) {
  qc.invalidateQueries({ queryKey: PIPELINES_KEY });
  qc.invalidateQueries({ queryKey: ["crm", "leads"] });
  qc.invalidateQueries({ queryKey: ["crm", "activities", "feed"] });
  qc.invalidateQueries({ queryKey: ["crm", "dashboard", "overview"] });
  qc.invalidateQueries({ queryKey: ["crm", "pipelines", "analytics"] });
}

export function useCreatePipeline() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => (await crmApi.post("/pipelines", payload)).data,
    onSuccess: () => invalidate(qc),
  });
}

export function useUpdatePipeline() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ pipelineId, patch }) =>
      (await crmApi.patch(`/pipelines/${pipelineId}`, patch)).data,
    onSuccess: () => invalidate(qc),
  });
}

export function useDeletePipeline() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (pipelineId) =>
      (await crmApi.delete(`/pipelines/${pipelineId}`)).data,
    onSuccess: () => invalidate(qc),
  });
}

export function useAddStage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ pipelineId, name }) =>
      (await crmApi.post(`/pipelines/${pipelineId}/stages`, { name })).data,
    onSuccess: () => invalidate(qc),
  });
}

export function useUpdateStage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ pipelineId, stageId, name }) =>
      (await crmApi.patch(`/pipelines/${pipelineId}/stages/${stageId}`, { name })).data,
    onSuccess: () => invalidate(qc),
  });
}

export function useDeleteStage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ pipelineId, stageId }) =>
      (await crmApi.delete(`/pipelines/${pipelineId}/stages/${stageId}`)).data,
    onSuccess: () => invalidate(qc),
  });
}

// Move a lead into a stage. Optimistic update so the card visually moves before
// the server roundtrip; we patch the cached pipelines payload and rollback on
// error. Source CRM did the same.
export function useMoveLead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ pipelineId, leadId, stageId, position }) =>
      (await crmApi.post(`/pipelines/${pipelineId}/move`, { leadId, stageId, position })).data,
    onMutate: async ({ pipelineId, leadId, stageId, position }) => {
      await qc.cancelQueries({ queryKey: PIPELINES_KEY });
      const snapshot = qc.getQueryData(PIPELINES_KEY);
      qc.setQueryData(PIPELINES_KEY, (prev) => {
        if (!prev?.pipelines) return prev;
        const next = JSON.parse(JSON.stringify(prev));
        const p = next.pipelines.find((pp) => String(pp._id) === String(pipelineId));
        if (!p) return next;
        let moving = null;
        p.stages.forEach((s) => {
          const idx = s.leads.findIndex((e) => String(e.leadId) === String(leadId));
          if (idx >= 0) {
            moving = s.leads[idx];
            s.leads.splice(idx, 1);
          }
        });
        const target = p.stages.find((s) => String(s._id) === String(stageId));
        if (!target) return next;
        const insertAt = typeof position === "number"
          ? Math.max(0, Math.min(position, target.leads.length))
          : target.leads.length;
        target.leads.splice(insertAt, 0, moving || { leadId, position: insertAt, lead: null });
        target.leads.forEach((e, i) => { e.position = i; });
        return next;
      });
      return { snapshot };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.snapshot) qc.setQueryData(PIPELINES_KEY, ctx.snapshot);
    },
    onSettled: (_data, _err, vars) => {
      invalidate(qc);
      if (vars?.leadId) qc.invalidateQueries({ queryKey: ["crm", "activities", vars.leadId] });
    },
  });
}

// Add many leads to a pipeline stage in one call. Returns a summary so the
// UI can show "12 added, 3 moved, 2 already there". `mode` is 'skip-existing'
// (default) or 'move' — server treats anything else as 'skip-existing'.
export function useBulkAddLeads() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ pipelineId, leadIds, stageId, mode = "skip-existing" }) =>
      (await crmApi.post(`/pipelines/${pipelineId}/leads/bulk`, { leadIds, stageId, mode })).data,
    onSuccess: (_data, vars) => {
      invalidate(qc);
      // Server logs one PIPELINE_MOVE per affected lead; refresh each lead's
      // per-lead activity timeline so an open drawer shows the new rows.
      for (const id of vars?.leadIds || []) {
        qc.invalidateQueries({ queryKey: ["crm", "activities", id] });
      }
    },
  });
}

export function usePipelineAnalytics(pipelineId, { enabled = true } = {}) {
  const { companyQueryParams } = useSelectedCompany();
  const scope = scopeParams(companyQueryParams);
  return useQuery({
    queryKey: ["crm", "pipelines", "analytics", pipelineId, scope],
    queryFn: async () =>
      (await crmApi.get(`/pipelines/${pipelineId}/analytics`, { params: scope })).data,
    enabled: !!pipelineId && enabled,
    staleTime: 30_000,
  });
}

export function useRemoveLeadFromPipeline() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ pipelineId, leadId }) =>
      (await crmApi.delete(`/pipelines/${pipelineId}/leads/${leadId}`)).data,
    onSuccess: (_data, vars) => {
      invalidate(qc);
      if (vars?.leadId) qc.invalidateQueries({ queryKey: ["crm", "activities", vars.leadId] });
    },
  });
}
